import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LockIcon, Trash2Icon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { pricingConfig } from '../../data/pricing';
import type { CartLine } from '../../types/cart';
import { addonQualifies, addonUnitPrice, betterBundleFor, bundleName, formatMoney, getBundle, priceLine, warrantyTierForLine } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { QuantityStepper } from '../../components/ui/QuantityStepper';
import { WarrantyTag } from '../../components/ui/WarrantyTag';
import { ProductAssetSlot } from '../../components/product/ProductAssetSlot';
import { PaymentTrust } from '../../components/shop/PaymentTrust';
export function Cart() {
  const {
    lines,
    totals
  } = useCart();
  const {
    t,
    lang
  } = useLanguage();
  const m = (n: number) => formatMoney(n, lang);
  return <div className="mx-auto max-w-site px-5 py-14 md:px-8 md:py-20" data-id="element-1191" data-mp="107rl7k5c5h7h">
      <h1 className="font-display text-5xl font-semibold uppercase md:text-6xl" data-id="element-1192" data-mp="v8elvzxa2ho0">{t('cart.title')}</h1>

      {lines.length === 0 ? <div className="mt-10 border-t mf-hairline pt-10" data-id="element-1193" data-mp="q8jyw7gmic88">
          <p className="text-lg text-titanium" data-id="element-1194" data-mp="qb4yeptg8g2t">{t('cart.empty')}</p>
          <div className="mt-6 flex flex-wrap gap-3" data-id="element-1195" data-mp="m9b5x4teqidq">
            <Link to="/systems" className={buttonClasses('primary')} data-id="element-1196" data-mp="1iyioc3yqcbdf">
              {t('paths.systems')}
            </Link>
            <Link to="/build" className={buttonClasses('secondary')} data-id="element-1197" data-mp="1dzxs0fmlmbu9">
              {t('paths.byo')}
            </Link>
          </div>
        </div> : <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_400px]" data-id="element-1198" data-mp="156uey8fv8105">
          <ul className="border-t mf-hairline" data-id="element-1199" data-mp="154i0445em6rb">
            {lines.map(l => <CartRow key={l.id} line={l} data-id="element-1200" data-mp="21q1ps6d3vhdc" />)}
          </ul>

          <aside aria-label="Order summary" className="lg:sticky lg:top-32 lg:self-start" data-id="element-1201" data-mp="1wwpgd3gzzh56">
            <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-8" data-id="element-1202" data-mp="3r5cfeazd4c6o">
              <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1203" data-mp="3w4flzzuzqqgr">Order summary</h2>
              <dl className="mt-6 space-y-3 text-sm" data-id="element-1204" data-mp="3hg2ogyg98zk5">
                <div className="flex justify-between" data-id="element-1205" data-mp="3hf9nyf7w36ag">
                  <dt className="text-titanium" data-id="element-1206" data-mp="3medx55utd40s">Total lights</dt>
                  <dd data-id="element-1207" data-mp="3mct63q7pk37o">{totals.lights}</dd>
                </div>
                <div className="flex justify-between" data-id="element-1208" data-mp="38ebkby1d50se">
                  <dt className="text-titanium" data-id="element-1209" data-mp="3ddxx66da4f0o">Savings vs. individual pricing</dt>
                  <dd className="text-ice" data-id="element-1210" data-mp="3dbuykxy29dkc">−{m(totals.savings)}</dd>
                </div>
                <div className="flex justify-between" data-id="element-1211" data-mp="2yj1j3cisgxfo">
                  <dt className="text-titanium" data-id="element-1212" data-mp="33h2zfpotying">Shipping</dt>
                  <dd className="text-titanium" data-id="element-1213" data-mp="wpkc8b2hg0lz">Calculated at checkout</dd>
                </div>
                <div className="flex justify-between" data-id="element-1214" data-mp="11ookmzhl1ub9">
                  <dt className="text-titanium" data-id="element-1215" data-mp="11ndjz8jfp2od">Taxes</dt>
                  <dd className="text-titanium" data-id="element-1216" data-mp="2m6fu1ynl02a0">Calculated at checkout</dd>
                </div>
              </dl>
              <div className="mt-6 flex items-baseline justify-between border-t mf-hairline pt-5" data-id="element-1217" data-mp="2h8e18m0csolc">
                <span className="text-titanium" data-id="element-1218" data-mp="2vwsdfnjntpek">{t('cart.subtotal')}</span>
                <span className="font-display text-4xl font-semibold" data-id="element-1219" data-mp="2r5seyduw6z9y">{m(totals.total)}</span>
              </div>
              <Link to="/checkout" className={`${buttonClasses('primary', 'lg')} mt-7 w-full`} data-id="element-1220" data-mp="34wnu27vziclz">
                <LockIcon className="h-4 w-4" data-id="element-1221" data-mp="30w4zw76mcugm" /> {t('cart.checkout')}
              </Link>
              <p className="mt-3 text-[12px] leading-relaxed text-titanium/70" data-id="element-1222" data-mp="3ev4ojeu4t9cs">
                By continuing to checkout you agree to our{' '}
                <Link to="/terms" className="underline hover:text-white" data-id="element-1223" data-mp="39xlaog1ciyld">Terms of Service</Link> (including binding individual
                arbitration), <Link to="/returns" className="underline hover:text-white" data-id="element-1224" data-mp="1kj6p24lmjru3">Return Policy</Link> and{' '}
                <Link to="/warranty" className="underline hover:text-white" data-id="element-1225" data-mp="1gzg2jcoi2eai">Warranty</Link>, and acknowledge our{' '}
                <Link to="/privacy" className="underline hover:text-white" data-id="element-1226" data-mp="1lxzp9fax3u3x">Privacy Policy</Link>.
              </p>
              <PaymentTrust className="mt-5" data-id="element-1227" data-mp="1qxcc401sh16i" />
              <p className="mt-5 text-xs leading-relaxed text-titanium/70" data-id="element-1228" data-mp="1qvj1yaryvytr">
                Every M1 PRO is backed by the MarineForce Promise — professional-grade support after your purchase. Questions?{' '}
                <Link to="/support" className="text-ice hover:underline" data-id="element-1229" data-mp="y73o3rkknmk0">
                  Contact support
                </Link>
                .
              </p>
            </div>
          </aside>
        </div>}
    </div>;
}
function CartRow({
  line
}: {
  line: CartLine;
}) {
  const {
    setIndividualQty,
    setExtras,
    remove,
    addSystem
  } = useCart();
  const {
    lang
  } = useLanguage();
  const m = (n: number) => formatMoney(n, lang);
  const p = priceLine(line);
  const better = line.kind === 'system' && line.extras > 0 ? betterBundleFor(line.bundleId, line.extras) : null;
  const title = line.kind === 'system' ? bundleName(getBundle(line.bundleId), lang) : 'M1 PRO — Build Your Own';
  return <li className="grid grid-cols-[88px_1fr] gap-5 border-b mf-hairline py-7 md:grid-cols-[112px_1fr_auto]" data-id="element-1230" data-mp="2or4whfzni26a">
      <ProductAssetSlot view="front" compact className="aspect-square w-full" data-id="element-1231" data-mp="2jsae2f5lifmp" />
      <div data-id="element-1232" data-mp="2juef8pc410ja">
        <h2 className="font-display text-2xl font-semibold uppercase leading-tight" data-id="element-1233" data-mp="2esxk7agb48am">{title}</h2>
        <p className="mt-1 text-sm text-titanium" data-id="element-1234" data-mp="2a28rjrsw559w">
          {line.kind === 'system' ? `${getBundle(line.bundleId).qty} lights${line.extras ? ` + ${line.extras} bundle-exclusive extra${line.extras > 1 ? 's' : ''} at ${m(addonUnitPrice())} each` : ''} · ${getBundle(line.bundleId).qty + line.extras} Magnetic Mount System bases` : `${line.qty} ${line.qty === 1 ? 'light' : 'lights'} · ${line.qty} Magnetic Mount System ${line.qty === 1 ? 'base' : 'bases'}`}
        </p>
        <div className="mt-3" data-id="element-1235" data-mp="1hfqtx9ks99li">
          <WarrantyTag tier={warrantyTierForLine(line)} data-id="element-1236" data-mp="1hfhdodpjigt0" />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-1237" data-mp="1meu1b0o8jrwn">
          {line.kind === 'individual' && <QuantityStepper value={line.qty} onChange={v => setIndividualQty(line.id, v)} min={1} max={pricingConfig.maxIndividualQty} label="lights" data-id="element-1238" data-mp="18fc9sdjk20jl" />}
          {line.kind === 'system' && addonQualifies(line.bundleId) && <div className="flex items-center gap-3" data-id="element-1239" data-mp="254a1tnzx7kd2">
              <span className="text-sm text-titanium" data-id="element-1240" data-mp="257605dfokb21">Extra lights</span>
              <QuantityStepper value={line.extras} onChange={v => setExtras(line.id, v)} min={0} max={pricingConfig.addon.maxQty} label="extra lights" data-id="element-1241" data-mp="1rdoglsmzuyc0" />
            </div>}
          <button type="button" onClick={() => remove(line.id)} className="inline-flex items-center gap-1.5 text-sm text-titanium hover:text-white" data-id="element-1242" data-mp="2nwuouosxuv8y">
            <Trash2Icon className="h-4 w-4" data-id="element-1243" data-mp="7gpu1crnmf19" /> Remove
          </button>
        </div>

        {better && line.kind === 'system' && <div className="mt-5 rounded-lg border border-marine/40 bg-marine/[0.08] p-4 text-sm" data-id="element-1244" data-mp="m8q7ubmtqvun">
            <p className="text-white" data-id="element-1245" data-mp="h9nevpk4cg31">
              The {bundleName(better.bundle, lang)} gives you {better.bundle.qty} lights for {m(better.total)}
              {better.saves > 0 ? ` — ${m(better.saves)} less` : ''}.
            </p>
            <button type="button" onClick={() => {
          remove(line.id);
          addSystem(better.bundle.id, 0);
        }} className="mt-2 inline-flex items-center gap-1 font-semibold text-ice hover:text-white" data-id="element-1246" data-mp="3fw0a2rj660g">
              Switch to {better.bundle.qty}-light system <ArrowRightIcon className="h-4 w-4" data-id="element-1247" data-mp="19vg93arcmme7" />
            </button>
          </div>}
      </div>
      <div className="col-span-2 text-right md:col-span-1" data-id="element-1248" data-mp="33gjl3b1ocw7u">
        <span className="font-display text-3xl font-semibold" data-id="element-1249" data-mp="38dicvmxv8r2v">{m(p.total)}</span>
        {p.savings > 0 && <p className="text-sm text-ice" data-id="element-1250" data-mp="3dcmjc5teoapi">Save {m(p.savings)}</p>}
      </div>
    </li>;
}